import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@components/ui/Button';
import { selectActiveAccount, useAuthStore } from '@features/auth/store/authStore';
import type { ApiError } from '@/types/api.types';

import { activateAccount } from '../api/accountsApi';
import { useBusinessSettingsStore } from '../store/businessSettingsStore';

type ActivationState = 'loading' | 'success' | 'error';

function CheckCircle() {
  return (
    <div
      className="mx-auto flex h-16 w-16 items-center justify-center rounded-full"
      style={{ backgroundColor: 'hsl(158 21% 93%)' }}
    >
      <svg viewBox="0 0 32 32" fill="none" className="h-8 w-8" aria-hidden="true">
        <path
          d="M6 16 L13 23 L26 10"
          stroke="hsl(160 51% 24%)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

function NextItem({ text }: { text: string }) {
  return (
    <li className="flex items-center gap-3 text-sm text-foreground">
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: 'hsl(158 21% 93%)' }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 16 16" fill="none" className="h-3 w-3">
          <path
            d="M4 8h8M9 5l3 3-3 3"
            stroke="hsl(160 51% 24%)"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      {text}
    </li>
  );
}

function Spinner() {
  return (
    <svg
      className="h-5 w-5 animate-spin"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  );
}

export function Step5Done() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeAccount = useAuthStore(selectActiveAccount);
  const setActiveAccount = useAuthStore((s) => s.setActiveAccount);
  const resetOnboarding = useBusinessSettingsStore((s) => s.reset);
  const [activationState, setActivationState] = useState<ActivationState>('loading');

  const activate = useCallback(() => {
    if (!activeAccount) return;
    setActivationState('loading');
    activateAccount(activeAccount.accountId)
      .then((res) => {
        setActiveAccount({ ...activeAccount, status: res.data.account.status });
        setActivationState('success');
      })
      .catch((err: ApiError) => {
        if (err.code === 'INVALID_STATE_TRANSITION') {
          setActiveAccount({ ...activeAccount, status: 'ACTIVE' });
          setActivationState('success');
        } else {
          setActivationState('error');
        }
      });
  }, [activeAccount, setActiveAccount]);

  useEffect(() => {
    activate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="text-center">
      <CheckCircle />

      <h2 className="mt-6 font-display text-[2rem] font-semibold leading-tight text-foreground">
        {t('onboarding.step5.title')}
      </h2>
      <p className="mt-2 text-sm text-muted-foreground">{t('onboarding.step5.subtitle')}</p>

      <ul className="mx-auto mt-8 max-w-xs space-y-3 text-left">
        <NextItem text={t('onboarding.step5.next1')} />
        <NextItem text={t('onboarding.step5.next2')} />
        <NextItem text={t('onboarding.step5.next3')} />
      </ul>

      <div className="mt-10">
        {activationState === 'loading' && (
          <Button size="lg" className="px-10" disabled>
            <Spinner />
            <span className="ml-2">{t('onboarding.step5.activating')}</span>
          </Button>
        )}

        {activationState === 'success' && (
          <Button size="lg" className="px-10" onClick={() => { resetOnboarding(); navigate('/account'); }}>
            {t('onboarding.step5.goToDashboard')}
          </Button>
        )}

        {activationState === 'error' && (
          <div className="space-y-3">
            <p className="text-sm text-destructive">{t('onboarding.step5.activationError')}</p>
            <Button size="lg" className="px-10" onClick={activate}>
              {t('onboarding.step5.retry')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
